import { useRef, useCallback, useEffect, useState } from 'react';
import {
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { VideoFrameUploadButtonProps } from './VideoFrameUploadButton.types';
import type { VideoFrame } from '../ChatOptions/VideoGeneration/VideoGenerationOptions.types';
import { getFileDownloadUrl } from '../../api/chats.api';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import { logger } from '@/lib/utils/logger';
import { notificationService } from '@/lib/services/notification.service';

async function uploadFrameFile(file: File): Promise<VideoFrame> {
  const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  await uploadFileToS3(url, formData);
  const downloadUrl = await getFileDownloadUrl(assetId);

  return {
    assetId,
    mimeType: file.type,
    url: downloadUrl,
    metadata: {
      fileName: file.name,
      size: file.size,
    },
  };
}

export const VideoFrameUploadButton = ({
  label,
  frame,
  onChange,
  onLoading = () => {},
  onFilePickerOpenChange = () => {},
  disabled = false,
}: VideoFrameUploadButtonProps) => {
  const { t } = useTranslation('chats');

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const hasFrame = !!frame;
  const isInteractive = !hasFrame && !isUploading && !disabled;

  const handleClick = useCallback(() => {
    if (!isInteractive) return;
    onFilePickerOpenChange(true);
    inputRef.current?.click();
  }, [isInteractive, onFilePickerOpenChange]);

  // `cancel` is not in React's input typings, so it is attached natively. Without it, dismissing
  // the dialog without choosing a file would leave the popup pinned open.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const onCancel = () => onFilePickerOpenChange(false);
    input.addEventListener('cancel', onCancel);
    return () => input.removeEventListener('cancel', onCancel);
  }, [onFilePickerOpenChange]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilePickerOpenChange(false);
      const file = event.target.files?.[0];
      if (!file) return;

      onLoading(true);
      setIsUploading(true);

      try {
        const uploaded = await uploadFrameFile(file);
        onChange(uploaded);
      } catch (err) {
        logger.error('Failed to upload frame file', err);
        notificationService.showError(
          t('chats.videoFrameUploadButton.failedToUpload', { label: label.toLowerCase() }),
        );
      } finally {
        setIsUploading(false);
        onLoading(false);
        event.target.value = '';
      }
    },
    [onChange, onLoading, onFilePickerOpenChange, label, t],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  let tooltipTitle: string;
  if (isUploading) {
    tooltipTitle = t('chats.videoFrameUploadButton.uploading', { label: label.toLowerCase() });
  } else if (hasFrame) {
    tooltipTitle = label;
  } else {
    tooltipTitle = t('chats.videoFrameUploadButton.attach', { label: label.toLowerCase() });
  }

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {hasFrame ? (
              // Two-sibling layout when a frame is attached: an inert row + a
              // dedicated clear button. Avoids nested-button a11y warnings.
              <div
                aria-label={label}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card text-sm"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                  {frame.url ? (
                    <img src={frame.url} alt={label} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-base leading-none">+</span>
                  )}
                </span>
                <span className="text-xs text-foreground truncate max-w-40">
                  {frame.metadata.fileName}
                </span>
                {!isUploading && (
                  <button
                    type="button"
                    aria-label={t('chats.videoFrameUploadButton.clear', {
                      label: label.toLowerCase(),
                    })}
                    onClick={handleClear}
                    disabled={disabled}
                    className="ml-auto inline-flex items-center justify-center h-5 w-5 rounded-full border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                aria-label={label}
                aria-busy={isUploading}
                disabled={!isInteractive}
                onClick={handleClick}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                  {isUploading ? <Spinner /> : <span className="text-base leading-none">+</span>}
                </span>
                <span className="text-xs text-foreground">
                  {t('chats.videoFrameUploadButton.attach', { label: label.toLowerCase() })}
                </span>
              </button>
            )}
          </TooltipTrigger>
          <TooltipContent side="top">{tooltipTitle}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid="video-frame-file-input"
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFileChange(e);
        }}
      />
    </>
  );
};
